import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle, Eye, EyeOff, ArrowLeft } from "lucide-react";

import { FechadaNaManutencao } from "@/modules/maintenance/components/FechadaNaManutencao";

// `VITE_API_URL` JÁ termina em `/api/v1` (produção: "/api/v1"); o caminho nunca
// leva o prefixo. Somá-lo de novo gerava /api/v1/api/v1/... → 401 e nenhum e-mail
// de redefinição saía (28/09/2026).
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1";

// Password strength rules matching backend check_password_strength()
function getPasswordStrength(password: string) {
  let score = 0;
  const feedback: string[] = [];

  if (password.length >= 8) score++;
  else feedback.push("Mínimo 8 caracteres");

  if (/[A-Z]/.test(password)) score++;
  else feedback.push("1 letra maiúscula");

  if (/[a-z]/.test(password)) score++;
  else feedback.push("1 letra minúscula");

  if (/\d/.test(password)) score++;
  else feedback.push("1 número");

  if (/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password)) score++;
  else feedback.push("1 caractere especial");

  return { score, feedback };
}

const strengthColors = [
  "bg-red-500",
  "bg-red-400",
  "bg-orange-400",
  "bg-yellow-400",
  "bg-green-400",
  "bg-green-600",
];
const strengthLabels = ["Muito fraca", "Fraca", "Regular", "Boa", "Forte", "Muito forte"];

const ResetPasswordConteudo = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [tokenInvalid, setTokenInvalid] = useState(false);

  const strength = getPasswordStrength(password);

  // Redirect to login after success
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => navigate("/login"), 3000);
      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("As senhas não coincidem.");
      return;
    }

    if (strength.score < 3) {
      setError(`Senha muito fraca. Faltam: ${strength.feedback.join(", ")}`);
      return;
    }

    setIsLoading(true);

    try {
      const resp = await fetch(`${API_URL}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });

      // 503 de manutenção (PR 2, 28/09/2026): o backend recusa este endpoint
      // quando a manutenção trava — o login mostra o aviso, então só volta
      // pra lá. `.catch(() => null)`: um 503 sem corpo JSON não pode quebrar.
      if (resp.status === 503) {
        const corpo: { maintenance?: boolean } | null = await resp.json().catch(() => null);
        if (corpo?.maintenance === true) {
          navigate("/login", { replace: true });
          return;
        }
      }

      if (resp.status === 400) {
        setTokenInvalid(true);
        return;
      }

      if (resp.status === 422) {
        const data = await resp.json();
        setError(data.detail || "Senha muito fraca.");
        return;
      }

      if (!resp.ok) {
        setError("Erro ao redefinir senha. Tente novamente.");
        return;
      }

      setSuccess(true);
    } catch {
      setError("Erro de conexão. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  // No token → invalid
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-3 sm:p-4 md:p-6 lg:p-8">
        <div className="w-full max-w-xs sm:max-w-sm md:max-w-md">
          <Card>
            <CardContent className="pt-6 text-center space-y-4">
              <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
              <h3 className="text-lg font-semibold">Link inválido</h3>
              <p className="text-sm text-gray-600">
                O link de recuperação é inválido ou está incompleto.
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate("/recuperar-senha")}
              >
                Solicitar novo link
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-3 sm:p-4 md:p-6 lg:p-8">
        <div className="w-full max-w-xs sm:max-w-sm md:max-w-md">
          <Card>
            <CardContent className="pt-6 text-center space-y-4">
              <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Senha alterada com sucesso!</h3>
              <p className="text-sm text-gray-600">Redirecionando para o login em 3 segundos...</p>
              <Button variant="outline" className="w-full" onClick={() => navigate("/login")}>
                Ir para o login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (tokenInvalid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-3 sm:p-4 md:p-6 lg:p-8">
        <div className="w-full max-w-xs sm:max-w-sm md:max-w-md">
          <Card>
            <CardContent className="pt-6 text-center space-y-4">
              <AlertCircle className="mx-auto h-12 w-12 text-orange-500" />
              <h3 className="text-lg font-semibold">Link expirado ou já utilizado</h3>
              <p className="text-sm text-gray-600">Solicite um novo link de recuperação.</p>
              <Button className="w-full" onClick={() => navigate("/recuperar-senha")}>
                Solicitar novo link
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-3 sm:p-4 md:p-6 lg:p-8">
      <div className="w-full max-w-xs sm:max-w-sm md:max-w-md space-y-4 sm:space-y-6 md:space-y-8">
        <div className="text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-gray-900">
            Redefinir Senha
          </h2>
          <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-600">
            Crie uma nova senha para sua conta
          </p>
        </div>

        <Card className="w-full">
          <CardHeader className="pb-3 sm:pb-4">
            <CardTitle className="text-base sm:text-lg md:text-xl">Nova Senha</CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Sua senha deve ter no mínimo 8 caracteres com letras maiúsculas, números e caracteres
              especiais
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4 md:space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                  <AlertDescription className="text-xs sm:text-sm">{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-1 sm:space-y-2">
                <Label htmlFor="password" className="text-xs sm:text-sm">
                  Nova senha
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Digite sua nova senha"
                    required
                    disabled={isLoading}
                    className="text-xs sm:text-sm min-h-[36px] sm:min-h-[40px]"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                    )}
                  </button>
                </div>

                {/* Password strength indicator */}
                {password && (
                  <div className="space-y-1">
                    <div className="flex gap-1">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className={`h-1.5 flex-1 rounded-full transition-colors ${
                            i < strength.score ? strengthColors[strength.score] : "bg-gray-200"
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-gray-500">
                      Força: {strengthLabels[strength.score]}
                      {strength.feedback.length > 0 && ` — Faltam: ${strength.feedback.join(", ")}`}
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-1 sm:space-y-2">
                <Label htmlFor="confirmPassword" className="text-xs sm:text-sm">
                  Confirmar nova senha
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirm ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirme sua nova senha"
                    required
                    disabled={isLoading}
                    className="text-xs sm:text-sm min-h-[36px] sm:min-h-[40px]"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirm(!showConfirm)}
                  >
                    {showConfirm ? (
                      <EyeOff className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                    )}
                  </button>
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-xs text-red-500">As senhas não coincidem</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full text-xs sm:text-sm md:text-base min-h-[36px] sm:min-h-[40px]"
                disabled={isLoading || !password || !confirmPassword}
              >
                {isLoading ? "Redefinindo..." : "Redefinir Senha"}
              </Button>
            </form>

            <div className="mt-3 sm:mt-4 md:mt-6 text-center">
              <Button
                variant="link"
                onClick={() => navigate("/login")}
                className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm min-h-[28px]"
              >
                <ArrowLeft className="h-3 w-3 mr-1" />
                Voltar ao login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

/** Invólucro: fecha a tela e volta pro /login durante a manutenção (o login
 * mostra o aviso) — ver `FechadaNaManutencao`. Os hooks da tela ficam todos
 * dentro de `ResetPasswordConteudo`, nunca depois de um return condicional. */
export default function ResetPassword() {
  return (
    <FechadaNaManutencao destino="/login">
      <ResetPasswordConteudo />
    </FechadaNaManutencao>
  );
}
